import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import * as authService from '../services/authService';
import { AuthModel } from '../services/authService';
import { notifySessionEnded } from './sessionEvents';
import { StorageKey, XStorage } from './storage';

/** Refresh when the access token has this long or less left. */
const REFRESH_THRESHOLD_MS = 60_000;
/** After a failed (non-401) attempt, this tab does not try again for this long. */
const FAILURE_BACKOFF_MS = 30_000;
const LOCK_NAME = 'xbit-admin-token-refresh';

/** The tab's single in-flight refresh, shared by every concurrent `getAuth()`. */
let inFlight: Promise<AuthModel | null> | null = null;
let lastFailureAt = 0;

/** Milliseconds until the token's `exp`, or null when it cannot be read. */
function msLeft(token: string | undefined): number | null {
  if (!token) return null;
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === 'number' ? exp * 1000 - Date.now() : null;
  } catch {
    return null;
  }
}

/** True when the access token is readable and has 60 s or less left. */
function accessTokenNeedsRefresh(auth: AuthModel): boolean {
  const left = msLeft(auth.accessToken);
  return left !== null && left <= REFRESH_THRESHOLD_MS;
}

function refreshTokenUsable(auth: AuthModel): boolean {
  const left = msLeft(auth.refreshToken);
  return left !== null && left > 0;
}

/** The refresh itself. Runs inside the Web Lock where one exists. */
async function refreshOnce(): Promise<AuthModel | null> {
  // Re-read after the lock: another tab may have rotated, logged out, or signed in as someone else.
  const current = await XStorage.get<AuthModel>(StorageKey.AuthData);
  if (!current) return null;
  if (!accessTokenNeedsRefresh(current)) return current;
  if (!refreshTokenUsable(current)) return current;

  // Always the re-read token, never a value read before the lock was acquired.
  const sent = current.refreshToken;
  try {
    const next = await authService.refreshSession(sent);
    if (!next?.accessToken || !next?.refreshToken) {
      throw new Error('Refresh response is missing a token');
    }
    const stored = await XStorage.get<AuthModel>(StorageKey.AuthData);
    if (stored?.refreshToken === sent) {
      await XStorage.set(StorageKey.AuthData, next);
      lastFailureAt = 0;
      return next;
    }
    // Logout, or another user signed in, while the request was in flight.
    return stored;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      const stored = await XStorage.get<AuthModel>(StorageKey.AuthData);
      if (stored?.refreshToken === sent) {
        await XStorage.remove(StorageKey.AuthData);
        notifySessionEnded();
        return null;
      }
      return stored;
    }
    lastFailureAt = Date.now();
    return XStorage.get<AuthModel>(StorageKey.AuthData);
  }
}

function withLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? locks.request(LOCK_NAME, task) : task();
}

function refreshExclusively(): Promise<AuthModel | null> {
  return withLock(refreshOnce);
}

/**
 * The reactive refresh (XBIT-4826 D10.1), inside the lock. The server refused `refused`, so whatever is
 * stored now decides: a different access token means another tab or flight already rotated (no request);
 * the same one means this is the pair to rotate.
 */
async function refreshRefused(refused: string): Promise<AuthModel | null> {
  const current = await XStorage.get<AuthModel>(StorageKey.AuthData);
  if (!current) return null;
  if (current.accessToken !== refused) return current;
  if (!refreshTokenUsable(current)) return null;

  const sent = current.refreshToken;
  try {
    const next = await authService.refreshSession(sent);
    if (!next?.accessToken || !next?.refreshToken) {
      throw new Error('Refresh response is missing a token');
    }
    const stored = await XStorage.get<AuthModel>(StorageKey.AuthData);
    if (stored?.refreshToken === sent) {
      await XStorage.set(StorageKey.AuthData, next);
      lastFailureAt = 0;
      return next;
    }
    return stored;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      const stored = await XStorage.get<AuthModel>(StorageKey.AuthData);
      if (stored?.refreshToken === sent) {
        await XStorage.remove(StorageKey.AuthData);
        notifySessionEnded();
        return null;
      }
      // Another tab stored a newer pair while this request was in flight: use it.
      return stored;
    }
    // A 5xx or a network failure says nothing about the session: keep the stored pair.
    return null;
  }
}

/**
 * A pair newer than the refused access token, or null when there is none (sign-in is then up to the caller's
 * error handling). Shares the tab's in-flight promise and the cross-tab lock with the proactive refresh.
 */
export async function refreshAfterUnauthorized(refusedAccessToken: string): Promise<AuthModel | null> {
  if (inFlight) {
    const joined = await inFlight;
    if (!joined) return null;
    if (joined.accessToken !== refusedAccessToken) return joined;
  }
  if (!inFlight) {
    inFlight = withLock(() => refreshRefused(refusedAccessToken)).finally(() => {
      inFlight = null;
    });
  }
  const result = await inFlight;
  return result && result.accessToken !== refusedAccessToken ? result : null;
}

/**
 * The stored session, refreshed first when its access token is about to expire.
 * Returns the stored auth unchanged whenever refreshing is not possible or not needed, so the
 * existing `checkAuth` keeps deciding when the operator must log in again.
 */
export async function getAuthWithRefresh(): Promise<AuthModel | null> {
  const auth = await XStorage.get<AuthModel>(StorageKey.AuthData);
  if (!auth) return null;
  if (!accessTokenNeedsRefresh(auth)) return auth;
  if (!refreshTokenUsable(auth)) return auth;
  if (Date.now() - lastFailureAt < FAILURE_BACKOFF_MS) return auth;

  if (!inFlight) {
    inFlight = refreshExclusively().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Test hook: forget module state between cases. */
export function resetTokenRefreshStateForTests() {
  inFlight = null;
  lastFailureAt = 0;
}
